import { Box, Stack, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { CampaignSavedData } from "../../../types/apiTypes";
import {
  AddPreferencesStep,
  ConfirmAndCustomizeStep,
  TargetAccountsStep,
  UploadProductInfoStep,
  DefineTargetJobTitlesStep,
  StepsBlock,
} from "./steps";

const initValues: CampaignSavedData = {
  company_name: "",
  product_name: "",
  product_abstract: "",
  target_audience: "",
};

type SavedData = {
  data: CampaignSavedData;
  files: string[];
  url: string;
};

export const CreateCampaignFlow = ({
  setIsCreateFlowStart,
  returnBackHandler,
}: {
  setIsCreateFlowStart: (isStart: boolean) => void;
  returnBackHandler: () => void;
}) => {
  const [pdfFiles, setPdfFiles] = useState<File[] | null>(null);
  const [productUrl, setProductUrl] = useState<string>("");
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [resultData, setResultData] = useState<CampaignSavedData>(initValues);

  useEffect(() => {
    if (Object.values(resultData).some((el) => el !== "")) {
      setIsCreateFlowStart(true);
    }
  }, [resultData]);

  useEffect(() => {
    const savedData = localStorage.getItem("data");
    const step = localStorage.getItem("step");

    if (savedData) {
      const { data, files, url } = JSON.parse(savedData) as SavedData;

      setResultHandler(
        data,
        files?.map((el) => JSON.parse(el) as File) || null,
        url,
      );
      setCurrentStep(Number(step) || 1);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("step", JSON.stringify(currentStep));
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem(
      "data",
      JSON.stringify({
        data: resultData,
        files: pdfFiles?.map((file) =>
          JSON.stringify({
            name: file?.name,
            size: file?.size,
            type: file?.type,
            lastModified: file?.lastModified,
          }),
        ),
        url: productUrl,
      }),
    );
  }, [pdfFiles, productUrl, resultData]);

  const setResultHandler = (
    data: CampaignSavedData,
    files: File[] | null,
    url: string,
  ) => {
    // TODO: delete when backend is ready, also remove from type
    delete data.target_audience;
    setResultData(data);
    setPdfFiles(files);
    setProductUrl(url);
  };

  const steps: Record<string, ReactNode> = {
    "step-1": (
      <UploadProductInfoStep
        returnBackHandler={returnBackHandler}
        nextStepHandler={() => setCurrentStep(2)}
        setResultData={setResultHandler}
        initProductUrl={productUrl}
        initPDFFiles={pdfFiles || []}
      />
    ),
    "step-2": (
      <ConfirmAndCustomizeStep
        initData={resultData}
        backHandler={() => setCurrentStep(1)}
        nextStepHandler={() => setCurrentStep(3)}
        setResultData={setResultData}
      />
    ),
    "step-3": (
      <AddPreferencesStep
        initData={resultData}
        backHandler={() => setCurrentStep(2)}
        nextStepHandler={() => setCurrentStep(4)}
        setResultData={setResultData}
      />
    ),
    "step-4": (
      <DefineTargetJobTitlesStep
        initData={resultData}
        backHandler={() => setCurrentStep(3)}
        nextStepHandler={() => setCurrentStep(5)}
        setResultData={setResultData}
      />
    ),
    "step-5": (
      <TargetAccountsStep
        resultData={resultData}
        backHandler={() => setCurrentStep(4)}
      />
    ),
  };

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap="40px"
      width="100%"
      justifyContent="center"
    >
      <Box paddingTop="60px">
        <StepsBlock currentStep={currentStep} setCurrentStep={setCurrentStep} />
      </Box>

      <Stack
        maxWidth={{ xs: "100%", md: "858px" }}
        width="100%"
        alignItems={{ xs: "center", md: "flex-start" }}
      >
        <Typography
          variant="h4"
          color="custom.textDarkBlue"
          textAlign={{ xs: "center", md: "left" }}
          fontWeight={500}
          paddingBottom="24px"
        >
          New Buying Committee Analysis
        </Typography>

        {steps[`step-${currentStep}`]}
      </Stack>
    </Stack>
  );
};
